import { z } from "zod";

export const OrderStatusSchema = z.enum([
  "pending",
  "sent_to_kitchen",
  "preparing",
  "ready",
  "delivered",
  "cancelled",
]);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;

export const OrderSourceSchema = z.enum(["landing", "pos", "kiosk"]);
export type OrderSource = z.infer<typeof OrderSourceSchema>;

export const PaymentMethodSchema = z.enum(["cash", "card"]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const OrderPaymentSchema = z.object({
  id: z.string().uuid(),
  method: PaymentMethodSchema,
  amountCents: z.number().int().nonnegative(),
  tenderedCents: z.number().int().nonnegative().nullable(),
  changeCents: z.number().int().nonnegative().nullable(),
  paidAt: z.string().datetime(),
});
export type OrderPayment = z.infer<typeof OrderPaymentSchema>;

export const OrderTypeSchema = z.enum(["table", "online", "takeaway", "delivery"]);
export type OrderType = z.infer<typeof OrderTypeSchema>;

export const INGREDIENT_NAME_MAX = 40;
export const REMOVABLE_INGREDIENTS_MAX = 20;
export const LINE_NOTE_MAX = 140;

export const LineModifiersSchema = z.object({
  without: z.array(z.string().trim().min(1).max(INGREDIENT_NAME_MAX)).max(REMOVABLE_INGREDIENTS_MAX).optional(),
  note: z.string().trim().max(LINE_NOTE_MAX).optional(),
});
export type LineModifiers = z.infer<typeof LineModifiersSchema>;

export const OrderItemSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  menuItemId: z.string().uuid(),
  nameSnapshot: z.string(),
  shortNameSnapshot: z.string().nullable().default(null),
  priceCentsSnapshot: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  modifiers: LineModifiersSchema.default({}),
  additionId: z.string().uuid().nullable().default(null),
  additionClientId: z.string().uuid().nullable().default(null),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const OrderSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  source: OrderSourceSchema,
  status: OrderStatusSchema,
  /**
   * The number a customer and the kitchen say out loud. Assigned by the
   * server when the order is written, counted per store per business day, so
   * it stays short enough to call across a counter. Null only for orders
   * placed before ticket numbering existed.
   */
  ticketNumber: z.number().int().positive().nullable().default(null),
  /** The business day `ticketNumber` counts within, as YYYY-MM-DD. */
  ticketDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
  clientId: z.string().uuid().nullable().default(null),
  hubTicketNumber: z.number().int().positive().nullable().default(null),
  customerName: z.string().min(1).max(200),
  customerPhone: z.string().min(1).max(32),
  customerAddress: z.string().max(500).nullable().default(null),
  homeDelivery: z.boolean().default(false),
  totalCents: z.number().int().nonnegative(),
  currency: z.string().length(3).default("DZD"),
  notes: z.string().max(1000).nullable().default(null),
  createdByUserId: z.string().uuid().nullable().default(null),
  deliveredAt: z.string().datetime().nullable().default(null),
  cancelledAt: z.string().datetime().nullable().default(null),
  accountingDayId: z.string().uuid().nullable().default(null),
  tableId: z.string().uuid().nullable().default(null),
  tableLabel: z.string().max(40).nullable().default(null),
  payment: OrderPaymentSchema.nullable().default(null),
  items: z.array(OrderItemSchema).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Order = z.infer<typeof OrderSchema>;

export function orderType(
  order: Pick<Order, "tableId" | "homeDelivery" | "source">,
): OrderType {
  if (order.tableId) return "table";
  if (order.source === "landing") return "online";
  if (order.homeDelivery) return "delivery";
  return "takeaway";
}
